import type { AuthUser } from '@/types/api';

type UserRole = AuthUser['role'];

const ROLE_DEFAULTS: Record<UserRole, string> = {
  admin:      '/admin',
  supervisor: '/admin',
  uploader:   '/uploader',
  // F5-R9a: «Mi cola» vive DENTRO de Inicio — el agente aterriza en / directo
  // (evita el doble hop por el redirect de /queue). El prefijo /queue sigue
  // permitido abajo: los deep-links viejos caen en el redirect y llegan bien.
  agent:      '/',
  navigator:  '/navigator',
};

const ROLE_ALLOWED_PREFIXES: Record<UserRole, string[]> = {
  admin:      ['/admin', '/queue', '/uploader', '/screening', '/clients', '/settings'],
  supervisor: ['/admin', '/queue', '/uploader', '/screening', '/clients', '/settings'],
  agent:      ['/queue', '/screening', '/clients', '/pre-call', '/settings'],
  uploader:   ['/uploader', '/clients', '/settings'],
  // Navigators see member data by design (CEO 2026-07-12) but only through
  // their own module — no agent queue, no admin, no uploader review.
  navigator:  ['/navigator', '/closer', '/settings'],
};

export function getDefaultRouteForRole(role: UserRole): string {
  return ROLE_DEFAULTS[role] ?? '/';
}

/**
 * Resolve where to navigate after successful login. Honors an explicit
 * `from` (the URL the user was on before being bounced to /login) ONLY IF
 * the user's role is allowed to access that prefix; otherwise falls back to
 * the role's default landing.
 *
 * Prevents the v0.4.0 BUG-1 case where an uploader logging in on a tab that
 * had `/screening/19` open would land back on /screening/19 (403 on render).
 */
export function resolveLoginRedirect(role: UserRole, from: string | null | undefined): string {
  const roleDefault = getDefaultRouteForRole(role);
  if (!from || from === '/' || from === '/login') return roleDefault;
  const allowed = ROLE_ALLOWED_PREFIXES[role] ?? [];
  return allowed.some((p) => from.startsWith(p)) ? from : roleDefault;
}
